import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
// Aliased for the same reason as the Layer tile's actions: see scripts/check-dispatch-recursion.js.
import { fetchLayerLegend as fetchLayerLegendAction } from '../../../../../action/layer-legend-actions'
import { legendForDisplay } from '../../../../../lib/layerLegend'
import { isTimeLayer, timeLayerKind } from '../../../../../lib/timeLayers/timeline'
import { weatherLegendForDisplay } from '../../../../../lib/timeLayers/weatherLegends'
import LayerLegend from './legend'

// The (i) button on a layer tile and the popover it opens: the thumbnail, name,
// description and legend. A click rather than a hover, so it works on touch. The legend
// is fetched the first time the popover opens. Weather layers have no Layer/Legend: their
// tiles come coloured by gis-pipeline, so their legend is the time bar's, built here.

const POPOVER_WIDTH = 280
const GAP = 10
const MARGIN = 8

const place = rect => {
  const vw = window.innerWidth,
    fitsRight = rect.right + GAP + POPOVER_WIDTH + MARGIN <= vw,
    left = fitsRight ? rect.right + GAP : Math.max(MARGIN, vw - POPOVER_WIDTH - MARGIN)
  return { left, top: Math.max(MARGIN, rect.top - 12) }
}

/** The legend a weather layer draws, or null for any other layer. */
export const localLegend = layer => (isTimeLayer(layer) ? weatherLegendForDisplay(timeLayerKind(layer).kind) : null)

const LayerInfo = ({ layer, fetchLayerLegend }) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState(null)
  const [legend, setLegend] = useState({ status: 'idle', value: null })
  const buttonRef = useRef(null)
  const popoverRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const local = localLegend(layer)
    if (local) {
      setLegend({ status: 'done', value: local })
      return
    }
    let cancelled = false
    setLegend(l => (l.status === 'done' ? l : { status: 'loading', value: null }))
    fetchLayerLegend(layer.id)
      .then(value => !cancelled && setLegend({ status: 'done', value: legendForDisplay(value) }))
      .catch(() => !cancelled && setLegend({ status: 'error', value: null }))
    return () => { cancelled = true }
  }, [open, layer.id])

  useEffect(() => {
    if (!open) return
    const outside = e => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return
      if (buttonRef.current && buttonRef.current.contains(e.target)) return
      setOpen(false)
    }
    const escape = e => e.key === 'Escape' && setOpen(false)
    const close = () => setOpen(false)
    document.addEventListener('mousedown', outside)
    document.addEventListener('touchstart', outside)
    document.addEventListener('keydown', escape)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', outside)
      document.removeEventListener('touchstart', outside)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('resize', close)
    }
  }, [open])

  // Keep the popover on screen when it's taller than the space below the tile.
  useLayoutEffect(() => {
    if (!open || !popoverRef.current || !position) return
    const overflow = popoverRef.current.getBoundingClientRect().bottom - (window.innerHeight - MARGIN)
    if (overflow > 0) {
      const top = Math.max(MARGIN, position.top - overflow)
      if (top !== position.top) setPosition({ ...position, top })
    }
  })

  const toggle = e => {
    e.stopPropagation()
    if (!open && buttonRef.current) {
      setPosition(place(buttonRef.current.closest('.layer-container').getBoundingClientRect()))
    }
    setOpen(!open)
  }

  const image = layer.largeImageUrl || layer.imageUrl

  return (
    <>
      <button type="button" ref={buttonRef} className={`layer-info-btn${open ? ' is-open' : ''}`}
        aria-label={`About ${layer.name}`} aria-expanded={open} onClick={toggle}>i</button>
      {open && position && ReactDOM.createPortal(
        <div className="layer-info-popover" ref={popoverRef} role="dialog" aria-label={layer.name}
          style={{ left: position.left, top: position.top, width: POPOVER_WIDTH }}
          onClick={e => e.stopPropagation()}>
          <div className="layer-info-popover__head">
            {image && <img src={image} alt="" className="layer-info-popover__image" />}
            <div className="layer-info-popover__name">{layer.name}</div>
          </div>
          {layer.description && <p className="layer-info-popover__description">{layer.description}</p>}
          {legend.status === 'loading' && <div className="layer-info-popover__status">Loading legend…</div>}
          {legend.status === 'error' && <div className="layer-info-popover__status">Couldn't load the legend.</div>}
          {legend.status === 'done' && <LayerLegend legend={legend.value} />}
        </div>,
        document.body
      )}
    </>
  )
}

const mapDispatchToProps = dispatch => ({
  fetchLayerLegend: id => dispatch(fetchLayerLegendAction(id))
})

export default connect(null, mapDispatchToProps)(LayerInfo)
